import React, { useMemo } from "react";
import { ReligionTab } from "../types";
import { Sparkles, Quote, Calendar, RefreshCw } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";

interface DailyRitualAffirmationProps {
  activeReligion: ReligionTab;
}

interface QuoteItem {
  quote: string;
  source: string;
  originalText?: string;
  theme: string;
}

const AFFIRMATIONS: Record<ReligionTab, QuoteItem[]> = {
  hinduism: [
    {
      quote: "You have a right to perform your prescribed duties, but you are not entitled to the fruits of your actions.",
      source: "Bhagavad Gita 2.47",
      originalText: "कर्मण्येवाधिकारस्ते मा फलेषु कदाचन।",
      theme: "Selfless Action (Nishkama Karma)"
    },
    {
      quote: "Truth is One; the wise describe it in many ways.",
      source: "Rig Veda 1.164.46",
      originalText: "एकं सद्विप्रा बहुधा वदन्ति।",
      theme: "Universal Unity (Ekam Sat)"
    },
    {
      quote: "Lead me from the unreal to the real, from darkness to light, from death to immortality.",
      source: "Brihadaranyaka Upanishad 1.3.28",
      originalText: "असतो मा सद्गमय तमसो मा ज्योतिर्गमय।",
      theme: "Inner Enlightenment (Jyoti)"
    },
    {
      quote: "When meditation is mastered, the mind is unwavering like the flame of a lamp in a windless place.",
      source: "Bhagavad Gita 6.19",
      originalText: "यथा दीपो निवातस्थो नेते सोपमा स्मृता।",
      theme: "Stillness of Mind (Dhyana)"
    },
    {
      quote: "The entire world is one divine family.",
      source: "Maha Upanishad 6.72",
      originalText: "वसुधैव कुटुम्बकम्।",
      theme: "Global Family (Vasudhaiva Kutumbakam)"
    }
  ],
  islam: [
    {
      quote: "Verily, with hardship comes ease.",
      source: "Surah Ash-Sharh 94:6",
      originalText: "إِنَّ مَعَ ٱلْعُسْرِ يُسْرًۭا",
      theme: "Hope & Perseverance (Sabr)"
    },
    {
      quote: "So remember Me; I will remember you.",
      source: "Surah Al-Baqarah 2:152",
      originalText: "فَٱذْكُرُونِيٓ أَذْكُرْكُمْ",
      theme: "Divine Remembrance (Dhikr)"
    },
    {
      quote: "And He found you lost and guided you.",
      source: "Surah Ad-Duha 93:7",
      originalText: "وَوَجَدَكَ ضَآلًّۭا فَهَدَىٰ",
      theme: "Divine Guidance (Hidayah)"
    },
    {
      quote: "The best among you are those who have the best character.",
      source: "Sahih al-Bukhari 6029",
      originalText: "إِنَّ مِنْ خِيَارِكُمْ أَحْسَنَكُمْ أَخْلَاقًا",
      theme: "Nobility of Character (Akhlaq)"
    },
    {
      quote: "Speak good or remain silent.",
      source: "Hadith An-Nawawi 15",
      originalText: "مَنْ كَانَ يُؤْمِنُ بِاللَّهِ وَالْيَوْمِ الآخِرِ فَلْيَقُلْ خَيْرًا أَوْ لِيَصْمُتْ",
      theme: "Mindful Speech (Sidq)"
    }
  ],
  buddhism: [
    {
      quote: "Peace comes from within. Do not seek it without.",
      source: "Dhammapada (Shakyamuni Buddha)",
      originalText: "चित्तं दन्तं सुखावहं",
      theme: "Inner Tranquility (Samatha)"
    },
    {
      quote: "Mind is everything. What you think, you become.",
      source: "Dhammapada Verse 1",
      originalText: "मनोपुब्बङ्गमा धम्मा मनोसेट्ठा मनोमया",
      theme: "Mindfulness & Thought (Citta)"
    },
    {
      quote: "Radiate boundless love towards the entire world — above, below, and across — unhindered, without ill will or enmity.",
      source: "Karaniya Metta Sutta",
      originalText: "मेत्तञ्च सव्वलोकस्मिं मानसं भावये अपरिमाणं",
      theme: "Loving-Kindness (Metta)"
    },
    {
      quote: "Just as water fills a vessel drop by drop, the wise fill themselves with good, gathering it little by little.",
      source: "Dhammapada Verse 122",
      originalText: "उदबिन्दुनिपातेन उदकुम्भोपि पूरति",
      theme: "Accumulation of Merit (Punya)"
    },
    {
      quote: "In the end, only three things matter: how much you loved, how gently you lived, and how gracefully you let go.",
      source: "Buddhist Wisdom",
      originalText: "अनिश्चं विपस्सना",
      theme: "Non-Attachment (Anicca)"
    }
  ],
  christianity: [
    {
      quote: "Love is patient, love is kind. It does not envy, it does not boast, it is not proud.",
      source: "1 Corinthians 13:4",
      originalText: "Caritas patiens est, benigna est",
      theme: "Divine Charity (Agape)"
    },
    {
      quote: "Be still, and know that I am God.",
      source: "Psalm 46:10",
      originalText: "Vacate et videte quoniam ego sum Deus",
      theme: "Contemplative Peace (Silentium)"
    },
    {
      quote: "Ask and it will be given to you; seek and you will find; knock and the door will be opened to you.",
      source: "Matthew 7:7",
      originalText: "Petite, et dabitur vobis; quaerite, et invenietis",
      theme: "Faith & Prayer (Fides)"
    },
    {
      quote: "The Lord is my shepherd; I shall not want. He makes me lie down in green pastures.",
      source: "Psalm 23:1-2",
      originalText: "Dominus regit me, et nihil mihi deerit",
      theme: "Divine Trust (Providentia)"
    },
    {
      quote: "Blessed are the peacemakers, for they shall be called children of God.",
      source: "Matthew 5:9",
      originalText: "Beati pacifici, quoniam filii Dei vocabuntur",
      theme: "Beatitude of Peace (Pax)"
    }
  ],
  judaism: [
    {
      quote: "Love your neighbor as yourself.",
      source: "Leviticus 19:18",
      originalText: "וְאָהַבְתָּ לְרֵעֲךָ כָּמוֹךָ",
      theme: "Brotherly Love (Ahavat Yisrael)"
    },
    {
      quote: "If I am not for myself, who will be for me? And if I am only for myself, what am I? And if not now, when?",
      source: "Pirkei Avot 1:14 (Hillel)",
      originalText: "אִם אֵין אֲנִי לִי, מִי לִי? וּכְשֶׁאֲנִי לְעַצְמִי, מָה אֲנִי? וְאִם לֹא עַכְשָׁו, אֵימָתַי?",
      theme: "Self-Responsibility & Action"
    },
    {
      quote: "The whole world is a narrow bridge, and the essential thing is not to fear at all.",
      source: "Rabbi Nachman of Breslov",
      originalText: "כָּל הָעוֹלָם כֻּלּוֹ גֶּשֶׁר צַר מְאֹד, וְהָעִיקָר - לֹא לְפַחֵד כְּלָל",
      theme: "Courage & Faith (Emunah)"
    },
    {
      quote: "It is not incumbent upon you to complete the work, but neither are you free to desist from it.",
      source: "Pirkei Avot 2:16",
      originalText: "לֹא עָלֶיךָ הַמְּלָאכָה לִגְמֹר, וְלֹא אַתָּה בֶן חוֹרִין לְהִבָּטֵל מִמֶּנָּה",
      theme: "Continuous Service (Tikkun Olam)"
    },
    {
      quote: "Deeds of loving-kindness are equal in weight to all the commandments.",
      source: "Jerusalem Talmud, Peah 1:1",
      originalText: "גְּמִילוּת חֲסָדִים שְׁקוּלָה כְּנֶגֶד כָּל הַמִּצְוֹת",
      theme: "Loving-Kindness (Chesed)"
    }
  ],
  sikhism: [
    {
      quote: "Truth is the highest virtue, but higher still is truthful living.",
      source: "Guru Nanak Dev Ji (Sri Guru Granth Sahib, Ang 62)",
      originalText: "ਸਚਹੁ ਓਰੈ ਸਭੁ ਕੋ ਊਪਰਿ ਸਚੁ ਆਚਾਰੁ ॥",
      theme: "Truthful Living (Sach Achar)"
    },
    {
      quote: "Recognize the whole human race as one single family.",
      source: "Guru Gobind Singh Ji (Akal Ustat)",
      originalText: "ਮਾਨਸ ਕੀ ਜਾਤਿ ਸਬੈ ਏਕੈ ਪਹਿਚਾਨਬੋ ॥",
      theme: "Universal Brotherhood"
    },
    {
      quote: "One Creator of all, Eternal Truth, Creator Being, Without Fear, Without Hatred.",
      source: "Mool Mantar (Guru Nanak Dev Ji)",
      originalText: "ੴ ਸਤਿ ਨਾਮੁ ਕਰਤਾ ਪੁਰਖੁ ਨਿਰਭਉ ਨਿਰਵੈਰੁ",
      theme: "Divine Oneness (Ik Onkar)"
    }
  ],
  taoism: [
    {
      quote: "The Tao that can be told is not the eternal Tao. The name that can be named is not the eternal name.",
      source: "Lao Tzu (Tao Te Ching, Chapter 1)",
      originalText: "道可道非常道，名可名非常名。",
      theme: "Mystic Ineffability (Tao)"
    },
    {
      quote: "When you are content to be simply yourself and don't compare or compete, everyone will respect you.",
      source: "Lao Tzu (Tao Te Ching)",
      originalText: "夫唯不争，故天下莫能与之争。",
      theme: "Effortless Action (Wu Wei)"
    },
    {
      quote: "Nature does not hurry, yet everything is accomplished.",
      source: "Lao Tzu",
      originalText: "无为而无不为",
      theme: "Natural Flow & Harmony"
    }
  ],
  jainism: [
    {
      quote: "Ahimsa Paramo Dharma — Non-violence is the supreme spiritual duty and law.",
      source: "Mahavira (Tattvartha Sutra)",
      originalText: "अहिंसा परमो धर्मः",
      theme: "Non-Violence (Ahimsa)"
    },
    {
      quote: "Live and let live; hurt no living being through mind, speech, or body.",
      source: "Bhagwan Mahavira",
      originalText: "जीवो या जीवेउ (परस्परोपग्रहो जीवानाम्)",
      theme: "Universal Compassion (Kshama)"
    },
    {
      quote: "All soul entities are equal; possess friendship towards all beings and enmity toward none.",
      source: "Navkar Mantra Tradition",
      originalText: "मित्ती मे सव्वभूएसु, वैरं मज्झं न केणइ। खामेमि सव्वजीवे॥",
      theme: "Equanimity & Forgiveness (Kshamavani)"
    }
  ],
  bahai: [
    {
      quote: "The earth is but one country, and mankind its citizens.",
      source: "Bahá'u'lláh (Gleanings)",
      originalText: "العالم أرض واحدة، والبشر سكّانها",
      theme: "Global Unity & World Peace"
    },
    {
      quote: "So powerful is the light of unity that it can illuminate the whole earth.",
      source: "Bahá'u'lláh (Epistle to the Son of the Wolf)",
      originalText: "Oneness of Religion & Humanity",
      theme: "Divine Illumination"
    },
    {
      quote: "Let your vision be world-embracing, rather than confined to your own self.",
      source: "Bahá'u'lláh (Tablets of Bahá'u'lláh)",
      originalText: "Universal Harmony",
      theme: "World-Embracing Vision"
    }
  ],
  zoroastrianism: [
    {
      quote: "Good Thoughts, Good Words, Good Deeds (Humata, Hukhta, Hvarshta).",
      source: "Avestan Gathas (Yasna 12)",
      originalText: "हुमत, हुख़्त, ह्वर्श्त (Humata, Hukhta, Hvarshta)",
      theme: "Trifold Path of Truth (Asha)"
    },
    {
      quote: "Righteousness is the best good and it is happiness; happiness is to him who is righteous for the sake of best righteousness.",
      source: "Ashem Vohu Prayer",
      originalText: "अषेम् वोहू वहिश्तेम् अस्ती",
      theme: "Supreme Truth (Asha Vahishta)"
    },
    {
      quote: "May we be among those who renew and transform the world (Frashokereti).",
      source: "Yasna 30.9",
      originalText: "अत् तोई वयेम् ख़्यामा योई ईम् फ़षेम् करेनाउन् अहुम्",
      theme: "World Renewal (Frashokereti)"
    }
  ],
  shinto: [
    {
      quote: "Treasure the harmony among all living beings and reverence the kami in nature.",
      source: "Seishin Tradition (kami-no-michi)",
      originalText: "和をもって尊しとなす (Wa wo motte尊しとなす)",
      theme: "Universal Harmony (Wa)"
    },
    {
      quote: "Cleanse your heart, speech, and actions like crystal clear mountain spring water.",
      source: "Misogi Purification Wisdom",
      originalText: "祓い給い清め給え (Harai tamae Kiyome tamae)",
      theme: "Purification of Soul (Misogi)"
    },
    {
      quote: "With every sunrise, offer gratitude for the sunlight and life-sustaining breath of Heaven and Earth.",
      source: "Shinto Norito Practice",
      originalText: "天地の恵みに感謝す",
      theme: "Gratitude to Creation (Kami)"
    }
  ],
  indigenous_americas: [
    {
      quote: "We do not inherit the Earth from our ancestors; we borrow it from our children.",
      source: "Indigenous Elders Wisdom",
      originalText: "Mitakuye Oyasin",
      theme: "Seventh Generation Stewardship"
    },
    {
      quote: "All things are connected. Whatever befalls the Earth befalls the children of the Earth.",
      source: "Chief Seattle / Sacred Hoop Wisdom",
      originalText: "Mitakuye Oyasin (All My Relations)",
      theme: "Interconnected Web of Life"
    },
    {
      quote: "Honor the sacred in every breath, the Four Winds, the Mother Earth, and the Father Sky.",
      source: "Four Directions Prayer",
      originalText: "Sacred Wheel Alignment",
      theme: "Four Directions Harmony"
    }
  ],
  shamanism: [
    {
      quote: "Listen to the voice of Pachamama in the rustle of leaves and the steady beat of the sacred drum.",
      source: "Earth Wisdom Tradition",
      originalText: "Pachamama (Mother Earth)",
      theme: "Earth Resonance & Steady Heartbeat"
    },
    {
      quote: "Walk lightly upon the Earth, carrying peace in your heart and light in your steps.",
      source: "Sovereign Shamanic Path",
      originalText: "Sumak Kawsay (Good Living)",
      theme: "Harmonious Life Journey"
    },
    {
      quote: "The drumbeat connects your pulse to the fundamental frequency of the cosmos.",
      source: "Shamanic Sound Medicine",
      originalText: "Pachamama Beat",
      theme: "Cosmic Heartbeat Resonance"
    }
  ],
  universal: [
    {
      quote: "Out beyond ideas of wrongdoing and rightdoing, there is a field. I'll meet you there.",
      source: "Jalal ad-Din Rumi",
      originalText: "از کفر و ز اسلام برون صحراییست",
      theme: "Transcendent Oneness"
    },
    {
      quote: "The quiet mind is all that you need. All else will happen rightly, once your mind is quiet.",
      source: "Nisargadatta Maharaj",
      originalText: "Universal Presence",
      theme: "Stillness & Awareness"
    },
    {
      quote: "We are not human beings having a spiritual experience. We are spiritual beings having a human experience.",
      source: "Pierre Teilhard de Chardin",
      originalText: "Cosmic Consciousness",
      theme: "Spiritual Nature"
    },
    {
      quote: "In every atom of creation, the divine light shines eternally.",
      source: "Interfaith Heritage Wisdom",
      originalText: "Omnipresent Light",
      theme: "Sacred Creation"
    },
    {
      quote: "When the heart is pure, every sound becomes a chant, every step a pilgrimage, and every breath a prayer.",
      source: "Universal Mystic Tradition",
      originalText: "Eternal Harmony",
      theme: "Purity of Heart"
    }
  ]
};

export default function DailyRitualAffirmation({ activeReligion }: DailyRitualAffirmationProps) {
  // Seeded daily pseudo-random selector based on current date (YYYY-MM-DD)
  const todayData = useMemo(() => {
    const now = new Date();
    const dateStr = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
    
    // Hash function to turn YYYY-MM-DD into a numeric index
    let hash = 0;
    for (let i = 0; i < dateStr.length; i++) {
      hash = (hash << 5) - hash + dateStr.charCodeAt(i);
      hash |= 0;
    }
    const daySeed = Math.abs(hash);

    const list = AFFIRMATIONS[activeReligion] || AFFIRMATIONS.universal;
    const index = daySeed % list.length;
    const selectedQuote = list[index];

    const formattedDate = now.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric"
    });

    return {
      quote: selectedQuote,
      formattedDate,
      dayIndex: index + 1,
      totalQuotes: list.length
    };
  }, [activeReligion]);

  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-950/40 via-stone-900/90 to-stone-950 border border-amber-500/30 p-5 shadow-xl backdrop-blur-md">
      {/* Ambient background glow */}
      <div className="absolute -top-12 -right-12 w-40 h-40 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-12 -left-12 w-40 h-40 bg-orange-500/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-b border-stone-800 pb-3 mb-4">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-xl bg-amber-500/15 border border-amber-500/30 text-amber-400">
            <Sparkles className="w-4 h-4 animate-pulse" />
          </div>
          <div>
            <h3 className="text-sm font-bold tracking-wide text-amber-100 uppercase">
              Daily Sacred Affirmation
            </h3>
            <p className="text-[11px] text-stone-400 flex items-center gap-1.5 mt-0.5">
              <Calendar className="w-3 h-3 text-amber-400" />
              <span>{todayData.formattedDate}</span>
              <span className="text-stone-600">•</span>
              <span className="text-amber-300 font-medium">{todayData.quote.theme}</span>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 bg-stone-900/80 border border-stone-800 px-3 py-1 rounded-full text-[11px] font-mono text-stone-300">
          <RefreshCw className="w-3 h-3 text-amber-400 animate-spin-slow" />
          <span>Daily Seed #{todayData.dayIndex}/{todayData.totalQuotes}</span>
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={`${activeReligion}-${todayData.dayIndex}`}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.3 }}
          className="relative z-10 space-y-3"
        >
          {todayData.quote.originalText && (
            <p className="text-sm sm:text-base font-serif text-amber-300 font-semibold tracking-wide dir-rtl">
              "{todayData.quote.originalText}"
            </p>
          )}

          <div className="flex items-start gap-3">
            <Quote className="w-6 h-6 text-amber-500/40 shrink-0 rotate-180 mt-1" />
            <div className="space-y-1.5">
              <p className="text-sm sm:text-base font-medium text-stone-100 italic leading-relaxed">
                "{todayData.quote.quote}"
              </p>
              <p className="text-xs font-semibold text-amber-400 tracking-wide font-sans">
                — {todayData.quote.source}
              </p>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
